@use '@/styles/tokens' as *;

.form {
  width: 100%;
  padding: var(--space-6);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-8);
}

.titleGroup {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.backBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-1);
  color: inherit;
  border-radius: var(--radius-sm);
  flex-shrink: 0;

  &:hover {
    background: rgb(0 0 0 / 5%);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}

.title {
  font-family: $font-family-heading;
  font-size: $font-size-2xl;
  font-weight: 600;
  line-height: 1.2;
  margin: 0;
}

.actions {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-shrink: 0;
}

// Cream/accent-tint gradient pill carrying the active step title, count and progress bar.
.stepBar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-pill);
  background: linear-gradient(90deg, $color-accent1-200, $color-light-brown-100);
}

.stepTitle {
  color: $color-accent1-800;
  font-weight: 600;
  white-space: nowrap;
}

.stepCount {
  color: var(--text-muted);
  white-space: nowrap;
}

.progressTrack {
  position: relative;
  width: 80px; // fixed progress-bar width; no spacing token maps to 80px
  height: var(--space-1);
  border-radius: var(--radius-pill);
  background: $color-neutral-200;
  overflow: hidden;
}

.progressFill {
  position: absolute;
  inset: 0 auto 0 0;
  height: 100%;
  border-radius: var(--radius-pill);
  background: $color-accent1-700;
}

// Pill shape override for header action buttons (mirrors bundle-form).
.pill {
  border-radius: var(--radius-pill) !important;
  padding-inline: var(--space-5) !important;
}

.body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  column-gap: var(--space-6);
  align-items: start;
}

.fields {
  min-width: 0;
  max-width: 520px;
}

// Step 3's tier editor needs the wider Figma column (668px) so the three 177px inputs + delete fit
// on one row and "Discount Amount *" doesn't wrap.
.fieldsWide {
  max-width: 668px;
}

.overview {
  min-width: 0;
}

.stepStub {
  color: var(--text-muted);
  margin: 0;
}

// Applicability section (Figma 4612-1071799 / 4612-1073136). Vertical rhythm; the 12px gap is
// Figma's 13px between the question and its radio row (node 32:223095), nearest token.
.applicability {
  min-inline-size: 0;
  margin: 0 0 var(--space-5);
  padding: 0;
  border: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

// Shared 14px section heading (type: "Where…"; sub: "Applicable To"). Regular weight per Figma.
.sectionLabel {
  display: block;
  font-size: $font-size-md;
  font-weight: $font-weight-regular;
  line-height: normal;
  color: var(--text-primary);
}

.required {
  color: $color-error-900;
}

// Device/Bundle/Customer + Applicable-To rows: horizontal, selected label goes medium weight (Figma).
.radios {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--space-6);

  :global(.ant-radio-wrapper-checked) {
    font-weight: $font-weight-medium;
  }
}

// "* Apply this discount only to selected …" — muted 12px helper (Figma 11px; nearest token).
.helper {
  margin: 0;
  font-size: $font-size-xs;
  color: var(--text-disabled);
}

// Field-level error line (matches <FormField>'s error styling) — e.g. the cleared-trigger prompt.
.error {
  margin: 0;
  font-size: 11px;
  font-weight: 300;
  color: $color-error-900;
}

// Customer secondary "Applicable To" sub-section.
.applicableTo {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);

  .sectionLabel {
    color: var(--text-secondary);
  }
}

// Step-3 quantity rule (Figma 4612-1078490). Behaviour block + tier table stacked with wider rhythm.
.quantitySection {
  display: flex;
  flex-direction: column;
  gap: var(--space-8); // 32px between the behaviour block and the tier table (Figma)
  margin-top: var(--space-5);
}

.behaviourBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.tierTable {
  display: flex;
  flex-direction: column;
  gap: var(--space-4); // 16px between the header, rows, and "Add tier"
}

// Shared 4-column grid: Min Qty | Max Qty | Discount Amount | delete (Figma 4612-1081210: three inputs
// + 24px gaps + a 40px delete in the 668px column).
.tierHeader,
.tierRow {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr)) var(--space-10);
  column-gap: var(--space-6); // 24px (Figma)
}

// Column labels rendered once, above the input rows (matches the FormField label style).
.tierColLabel {
  padding-inline: var(--space-2);
  font-size: $font-size-md;
  font-weight: $font-weight-regular;
  color: var(--text-secondary);
}

// Inputs top-align; each cell's error sits BELOW its input so it never shifts sibling columns.
.tierRow {
  align-items: start;
}

.tierCell {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-inline-size: 0;
}

// Per-field error (matches <FormField>'s error styling).
.tierError {
  margin: 0;
  font-size: 11px;
  font-weight: 300;
  color: $color-error-900;
}

// 40×40 delete target with a 16px TrashIcon (Figma); top-aligns with the input (no label in the row).
.tierDelete {
  inline-size: var(--space-10);
  block-size: var(--space-10);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 0;
  cursor: pointer;
  border-radius: var(--radius-md);
  color: var(--text-tertiary);

  &:hover {
    background: rgb(0 0 0 / 5%);
    color: $color-error-900;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}

// Borderless "⊕ Add tier" (Figma: circled-plus 16px + 4px gap + Geist 14 regular).
.addTier {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  align-self: flex-start;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
  font-size: $font-size-md;
  font-weight: $font-weight-regular;
  color: var(--text-primary);

  &:hover {
    color: $color-accent1-800;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
    border-radius: var(--radius-sm);
  }
}
